import { useEffect, useState } from "react";
import { businessesApi, accountsApi } from "@/lib/api";
import { useAuth, isMaster, userBusinessIds } from "@/lib/auth";
import { toast } from "sonner";
import { Search } from "lucide-react";

/**
 * /accounts — Chart of Accounts admin page.
 * Business selector at the top. Master users toggle Visible/Hidden per row.
 * Non-master users just see the currently visible chart (read-only).
 */
export default function Accounts() {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [bizId, setBizId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [q, setQ] = useState("");

  // Show the business selector for master admins AND any user who has access
  // to more than one business (iter103 — was previously gated on isMaster only,
  // so multi-business accounting users couldn't switch entities here).
  const myBizIds = userBusinessIds(user);
  const canSwitch = isMaster(user) || myBizIds.length > 1;
  const visibleBusinesses = isMaster(user) ? businesses : businesses.filter((b) => myBizIds.includes(b.id));

  useEffect(() => {
    businessesApi.list().then((b) => {
      setBusinesses(b);
      setBizId(isMaster(user) ? (b[0]?.id || "") : (myBizIds[0] || ""));
    });
  }, [user]);

  const load = () => { if (bizId) accountsApi.list(bizId, { include_hidden: true }).then(setAccounts); };
  useEffect(load, [bizId]);

  const toggleHidden = async (a) => {
    try {
      await accountsApi.setHidden(a.id, !a.hidden);
      toast.success(a.hidden ? "Now visible to users" : "Now hidden from users");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filtered = accounts.filter((a) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (a.code || "").toLowerCase().includes(s) || (a.name || "").toLowerCase().includes(s) || (a.type || "").toLowerCase().includes(s);
  });
  const biz = businesses.find((b) => b.id === bizId);

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-espresso">Chart of accounts</h1>
          <p className="text-sm text-espresso-ink/60 mt-1">Toggle visibility to control what non-master users see in dropdowns.</p>
        </div>
        <div className="flex gap-2">
          {canSwitch && (
            <select data-testid="accounts-biz" value={bizId} onChange={(e) => setBizId(e.target.value)} className="px-3 py-2 bg-white border border-canvas-border text-sm brand-focus">
              {visibleBusinesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-espresso-ink/40" />
            <input data-testid="accounts-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by code, name, type" className="pl-9 pr-3 py-2 bg-white border border-canvas-border text-sm brand-focus min-w-[280px]" />
          </div>
        </div>
      </div>
      <div className="text-xs text-espresso-ink/50">
        Chart of accounts syncs from <b>Accounting → Master sync</b>. {filtered.length} of {accounts.length} shown.
      </div>
      {!biz?.connected && (
        <div className="p-3 bg-status-pending/10 border border-status-pending/30 text-status-pending text-sm">
          Connect {biz?.accounting === "xero" ? "Xero" : "Zoho Books"} and run Master Sync first.
        </div>
      )}
      <div className="bg-white border border-canvas-border overflow-x-auto shadow-soft">
        <table className="w-full min-w-[820px]">
          <thead className="bg-canvas text-xs uppercase tracking-wider text-espresso-ink/60">
            <tr>
              <th className="text-left px-6 py-3 w-28">Code</th>
              <th className="text-left px-6 py-3">Name</th>
              <th className="text-left px-6 py-3 w-40">Type</th>
              <th className="text-left px-6 py-3 w-40">External ID</th>
              <th className="text-right px-6 py-3 w-32">Visibility</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.id} className={`border-t border-canvas-border ${a.hidden ? "bg-canvas/60 text-espresso-ink/50" : ""}`}>
                <td className="px-6 py-3 font-mono text-xs text-espresso-ink/70">{a.code || "—"}</td>
                <td className="px-6 py-3 text-sm text-espresso">{a.name}</td>
                <td className="px-6 py-3 text-xs uppercase tracking-widest text-espresso-ink/60">{a.type || "—"}</td>
                <td className="px-6 py-3 text-xs font-mono text-espresso-ink/50">{(a.external_id || "").slice(0, 18)}{(a.external_id || "").length > 18 ? "…" : ""}</td>
                <td className="px-6 py-3 text-right">
                  {isMaster(user) ? (
                    <button
                      data-testid={`account-toggle-hidden-${a.id}`}
                      onClick={() => toggleHidden(a)}
                      className={`px-2 py-1 rounded text-[10px] uppercase tracking-widest font-semibold ${a.hidden ? "bg-status-duplicate/10 text-status-duplicate border border-status-duplicate/30" : "bg-status-synced/10 text-status-synced border border-status-synced/30"}`}
                    >
                      {a.hidden ? "Hidden" : "Visible"}
                    </button>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest text-espresso-ink/50">Visible</span>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-14 text-center text-espresso-ink/50">No accounts. Run Master Sync from the Accounting page.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
